// AuditLogSection — admin UI for audit log browsing.
// Mirrors AdminDepartments class names and patterns from Admin.tsx.

import { useState, useEffect } from "preact/hooks";
import {
  useAdminAudit,
  setAuditFilters,
  nextAuditPage,
  prevAuditPage,
} from "../../store/adminAudit";
import { api } from "../../lib/api";
import { toastError } from "../../lib/toast";

interface AdminUser {
  id: number;
  email: string;
  name: string;
  is_active: boolean;
  is_external: boolean;
  /** Bot-Account (Migration 0085) — optional, siehe lib/api.ts AdminUser. */
  is_bot?: boolean;
}

const ENTITY_TYPES = [
  "project",
  "phase",
  "task",
  "user",
  "space",
  "external_invite",
] as const;

const ACTIONS = [
  "created",
  "updated",
  "deleted",
  "status_changed",
  "assignee_changed",
  "update_posted",
  "visibility_changed",
  "member_added",
  "member_role_changed",
  "member_removed",
  "admin_access_private",
  "admin_user_updated",
  "admin_guest_deleted",
  "admin_guest_deactivated",
  "admin_invite_resent",
] as const;

export function AuditLogSection(): preact.JSX.Element {
  const audit = useAdminAudit();
  const { items, total, loading, error, filters, pageIndex, nextCursor } = audit;

  const [users, setUsers] = useState<AdminUser[]>([]);
  useEffect(() => {
    let cancelled = false;
    api<AdminUser[]>("/api/admin/users")
      .then((all) => {
        if (!cancelled) setUsers(all.filter((u) => u.is_active && !u.is_external));
      })
      .catch(() => {
        // Das Protokoll selbst laedt weiter; nur der Benutzer-Filter bleibt
        // leer. Ohne Hinweis liest sich das leere Auswahlfeld aber als „es
        // gibt keine Benutzer" — api() toastet GETs nicht.
        toastError("Benutzerliste für den Filter konnte nicht geladen werden.");
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Per-row expanded for changes JSON
  const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set());
  const toggleRow = (id: number): void => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  // Keyset paging has no absolute offset; derive the display range from the
  // page index and the number of rows actually returned for the current page.
  const start = total === 0 ? 0 : pageIndex * filters.limit + 1;
  const end = pageIndex * filters.limit + items.length;

  return (
    <section class="space-y-4">
      {/* Filter bar */}
      <div class="rounded-lg border border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-lowest)] p-4">
        <h3 class="text-sm font-medium text-[color:var(--color-on-surface)] mb-3">
          Filter
        </h3>
        <div class="flex flex-wrap gap-3">
          {/* Entity type */}
          <div class="flex flex-col gap-1">
            <label class="text-xs text-[color:var(--color-on-surface-variant)]">
              Objekttyp
            </label>
            <select
              aria-label="Objekttyp filtern"
              value={filters.entityType ?? ""}
              onChange={(e) =>
                setAuditFilters({
                  entityType: (e.target as HTMLSelectElement).value || undefined,
                })
              }
              class="rounded border border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-lowest)] px-2 py-1.5 text-base sm:text-sm text-[color:var(--color-on-surface)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)]"
            >
              <option value="">Alle</option>
              {ENTITY_TYPES.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </select>
          </div>

          {/* Action */}
          <div class="flex flex-col gap-1">
            <label class="text-xs text-[color:var(--color-on-surface-variant)]">
              Aktion
            </label>
            <select
              aria-label="Aktion filtern"
              value={filters.action ?? ""}
              onChange={(e) =>
                setAuditFilters({
                  action: (e.target as HTMLSelectElement).value || undefined,
                })
              }
              class="rounded border border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-lowest)] px-2 py-1.5 text-base sm:text-sm text-[color:var(--color-on-surface)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)]"
            >
              <option value="">Alle</option>
              {ACTIONS.map((a) => (
                <option key={a} value={a}>
                  {a}
                </option>
              ))}
            </select>
          </div>

          {/* User */}
          <div class="flex flex-col gap-1">
            <label class="text-xs text-[color:var(--color-on-surface-variant)]">
              Benutzer
            </label>
            <select
              aria-label="Benutzer filtern"
              value={filters.userId != null ? String(filters.userId) : ""}
              onChange={(e) => {
                const v = (e.target as HTMLSelectElement).value;
                setAuditFilters({ userId: v ? Number(v) : undefined });
              }}
              class="rounded border border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-lowest)] px-2 py-1.5 text-base sm:text-sm text-[color:var(--color-on-surface)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)]"
            >
              <option value="">Alle</option>
              {/* Native <option> erlaubt kein Badge — Bots werden im Text
                  gekennzeichnet. */}
              {users.map((u) => (
                <option key={u.id} value={String(u.id)}>
                  {u.name}
                  {u.is_bot ? " (Bot)" : ""} ({u.email})
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Table */}
      <div class="rounded-lg border border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-lowest)] overflow-x-auto">
        {loading ? (
          <p class="p-4 text-sm text-[color:var(--color-on-surface-variant)]">
            Lädt…
          </p>
        ) : error ? (
          <p class="p-4 text-sm text-[color:var(--color-error)]">{error}</p>
        ) : items.length === 0 ? (
          <p class="p-4 text-sm text-[color:var(--color-on-surface-variant)] italic">
            Keine Einträge gefunden.
          </p>
        ) : (
          <table class="w-full text-sm">
            <thead>
              <tr class="border-b border-[color:var(--color-outline-variant)] text-left text-xs text-[color:var(--color-on-surface-variant)]">
                <th class="px-4 py-2 font-medium">Zeitpunkt</th>
                <th class="px-4 py-2 font-medium">Benutzer</th>
                <th class="px-4 py-2 font-medium">Aktion</th>
                <th class="px-4 py-2 font-medium">Objekt</th>
                <th class="px-4 py-2 font-medium">Änderungen</th>
              </tr>
            </thead>
            <tbody class="divide-y divide-[color:var(--color-outline-variant)]">
              {items.map((entry) => {
                const expanded = expandedRows.has(entry.id);
                return (
                  <tr
                    key={entry.id}
                    class="hover:bg-[color:var(--color-surface-container-low)] transition-colors"
                  >
                    <td class="px-4 py-2.5 whitespace-nowrap text-[color:var(--color-on-surface-variant)]">
                      {new Date(entry.created_at).toLocaleString("de-DE")}
                    </td>
                    <td class="px-4 py-2.5 whitespace-nowrap text-[color:var(--color-on-surface)]">
                      {entry.user?.name ?? (
                        <span class="italic text-[color:var(--color-on-surface-variant)]">
                          System
                        </span>
                      )}
                    </td>
                    <td class="px-4 py-2.5 whitespace-nowrap">
                      <span class="rounded bg-[color:var(--color-surface-container)] px-1.5 py-0.5 text-xs text-[color:var(--color-on-surface-variant)]">
                        {entry.action ?? "—"}
                      </span>
                    </td>
                    <td class="px-4 py-2.5 whitespace-nowrap text-[color:var(--color-on-surface-variant)]">
                      {entry.entity_type ? (
                        <>
                          {entry.entity_type}
                          {entry.entity_id != null && (
                            <span class="ml-1 text-xs">
                              #{entry.entity_id}
                            </span>
                          )}
                        </>
                      ) : (
                        "—"
                      )}
                    </td>
                    <td class="px-4 py-2.5">
                      {entry.changes != null ? (
                        <button
                          type="button"
                          onClick={() => toggleRow(entry.id)}
                          class="text-xs text-[color:var(--color-primary)] hover:underline"
                        >
                          {expanded ? "Verbergen" : "Anzeigen"}
                        </button>
                      ) : (
                        <span class="text-xs text-[color:var(--color-on-surface-variant)]">
                          —
                        </span>
                      )}
                      {expanded && (
                        <pre class="mt-1 max-w-xs overflow-x-auto rounded bg-[color:var(--color-surface-container)] p-2 text-label text-[color:var(--color-on-surface-variant)] whitespace-pre-wrap">
                          {JSON.stringify(entry.changes, null, 2)}
                        </pre>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination */}
      {total > 0 && (
        <div class="flex items-center justify-between text-sm text-[color:var(--color-on-surface-variant)]">
          <span>
            Zeige {start}–{end} von {total}
          </span>
          <div class="flex gap-2">
            <button
              type="button"
              onClick={() => prevAuditPage()}
              disabled={pageIndex === 0}
              class="rounded border border-[color:var(--color-outline-variant)] px-3 py-1.5 text-sm text-[color:var(--color-on-surface)] hover:bg-[color:var(--color-surface-container-low)] disabled:opacity-40 transition-colors"
            >
              Zurück
            </button>
            <button
              type="button"
              onClick={() => nextAuditPage()}
              disabled={nextCursor == null}
              class="rounded border border-[color:var(--color-outline-variant)] px-3 py-1.5 text-sm text-[color:var(--color-on-surface)] hover:bg-[color:var(--color-surface-container-low)] disabled:opacity-40 transition-colors"
            >
              Weiter
            </button>
          </div>
        </div>
      )}
    </section>
  );
}
